import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, Modal, StatusBanner, Text, useToast } from "@wayflyer/flyui";
import { RhfTextField } from "@wayflyer/flyui-hook-form";
import { memo, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { styled } from "styled-components";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { useInviteHandler, useInviteModal } from "../../contexts/invite-context";
import { useHasPermissions, useIsBankingCustomer } from "../../queries/banking";
import { MemberRolesComponent } from "../member-roles/member-roles-component";
import { DEFAULT_MEMBER_ROLE, companyRoleLabels } from "../member-roles/member-roles-helpers";

import type { InviteFormFields, InviteModalBaseProps, InviteModalProps } from "./types";
import { emailAuthValidationSchema, rolesValidationSchema } from "./validation-schemas";

const StyledModalBody = styled.div<{ showRoles: boolean }>`
  min-height: ${({ showRoles }) => (showRoles ? "380px" : "0px")};
`;

const InviteModalBase = ({ isOpen, close, isBankingCustomer, modalConfig }: InviteModalBaseProps) => {
  const { inviteHandler, onSuccessCallback } = useInviteHandler();
  const intl = useIntl();
  const toaster = useToast();
  const canInviteUsers = useHasPermissions(["company.membership_add"]);
  const showRoles = isBankingCustomer;
  const isDisabled = isBankingCustomer && !canInviteUsers;
  const schema = Yup.object({
    ...emailAuthValidationSchema(intl),
    ...rolesValidationSchema(intl, showRoles),
  });
  const form = useForm<InviteFormFields>({
    defaultValues: { emails: "", role: isBankingCustomer ? DEFAULT_MEMBER_ROLE : undefined },
    resolver: yupResolver(schema),
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = useCallback(() => {
    form.reset();
    close();
  }, [close, form]);

  const handleConfirm = useCallback(
    async ({ emails, role }: InviteFormFields) => {
      setIsLoading(true);
      try {
        await inviteHandler(emails, role ? [role] : undefined, onSuccessCallback);

        handleClose();
        toaster &&
          toaster.positive(
            intl.formatMessage(
              {
                id: "invite-modal: Invite sent",
                defaultMessage: "Invitation email sent to {name}",
              },
              {
                name: emails,
              }
            )
          );
      } catch (error) {
        // TODO i18n here
        form.setError("emails", { type: "custom", message: error as string });
      }
      setIsLoading(false);
    },
    [form, handleClose, intl, inviteHandler, toaster, onSuccessCallback]
  );

  return (
    <Modal
      title={intl.formatMessage({
        id: "invite-modal: Invite colleagues",
        defaultMessage: "Invite colleagues to join your Wayflyer team",
      })}
      primaryAction={{
        content: intl.formatMessage({ id: "invite-modal: Send invite", defaultMessage: "Send invite" }),
        onAction: form.handleSubmit(handleConfirm),
        loading: isLoading,
        disabled: isDisabled,
      }}
      tertiaryAction={{
        content: intl.formatMessage({ id: "invite-modal: Cancel", defaultMessage: "Cancel" }),
        onAction: handleClose,
      }}
      isOpen={isOpen}
      onClose={handleClose}
    >
      {isDisabled ? (
        <Text>
          {intl.formatMessage(
            {
              id: "invite-modal: Invite colleagues disabled",
              defaultMessage:
                "Inviting colleagues is not possible for non-{admin} members. Please ask an {admin} member on your team to invite a colleague.",
            },
            {
              admin: companyRoleLabels["admin"],
            }
          )}
        </Text>
      ) : (
        <StyledModalBody showRoles={showRoles}>
          <Flex direction="column" gap="4" justify="flex-start">
            {modalConfig?.showBanner && (
              <StatusBanner
                variant={modalConfig.bannerVariant || "info"}
                title={modalConfig.bannerTitle}
                subheading={modalConfig.bannerMessage}
              />
            )}
            <RhfTextField<InviteFormFields>
              form={form}
              placeholder={intl.formatMessage({
                id: "invite-modal: auth email placeholder",
                defaultMessage: "example@domain.com",
              })}
              label={intl.formatMessage({
                id: "invite-modal: Invite Email addresses",
                defaultMessage: "Email address(es)",
              })}
              helperText={intl.formatMessage({
                id: "invite-modal: invite modal helper text",
                defaultMessage: "You can enter multiple email addresses separated with commas.",
              })}
              type="email"
              name="emails"
            />
            {showRoles ? <MemberRolesComponent form={form} /> : null}
          </Flex>
        </StyledModalBody>
      )}
    </Modal>
  );
};

const InviteModalWithBanking = ({ isOpen, close, modalConfig }: InviteModalProps) => {
  const { isBankingCustomer } = useIsBankingCustomer();
  return (
    <InviteModalBase isOpen={isOpen} close={close} isBankingCustomer={isBankingCustomer} modalConfig={modalConfig} />
  );
};

const InviteModalWithoutBanking = ({ isOpen, close, modalConfig }: InviteModalProps) => {
  return <InviteModalBase isOpen={isOpen} close={close} isBankingCustomer={false} modalConfig={modalConfig} />;
};

export const InviteModal = ({ isOpen, close, modalConfig }: InviteModalProps) => {
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  if (!customerDomain || customerDomain === "internal") {
    return <InviteModalWithoutBanking isOpen={isOpen} close={close} modalConfig={modalConfig} />;
  }

  return <InviteModalWithBanking isOpen={isOpen} close={close} modalConfig={modalConfig} />;
};

export const InviteModalRenderer = memo(() => {
  const { isOpen, closeModal, modalConfig } = useInviteModal();

  return <InviteModal isOpen={isOpen} close={closeModal} modalConfig={modalConfig} />;
});

InviteModalRenderer.displayName = "InviteModalRenderer";
